/**
 * Demo shift + attendance policy helpers for QA.
 * Auto-reseed is OFF — policies deleted by the user stay deleted.
 * Call installDemoAttendancePolicy() only when explicitly seeding for tests.
 */
import {
  POLICIES_STORAGE_KEY,
  SHIFTS_STORAGE_KEY,
  loadAttendancePolicies,
  loadAttendanceShifts,
} from "./attendancePolicyEngine";

export const DEMO_SHIFT_ID = "general";
export const DEMO_POLICY_ID = "demo-auto-attendance-v1";

const DEMO_SHIFT = {
  id: DEMO_SHIFT_ID,
  name: "General (9 AM – 6 PM)",
  startTime: "09:00",
  endTime: "18:00",
  breakMinutes: 60,
  paidBreakMinutes: 0,
  unpaidBreakMinutes: 60,
  totalBreakMinutes: 60,
  isDefault: true,
};

/** Matches AttendancePolicyComponent default policy shape (active, all employees). */
const DEMO_POLICY = {
  id: DEMO_POLICY_ID,
  policyName: "Demo Auto Attendance (Test)",
  status: "active",
  isDefault: true,
  isDemo: true,
  allEmployees: true,
  effectiveDate: "2020-01-01",
  shiftGroupId: DEMO_SHIFT_ID,
  graceCheckInMinutes: 15,
  graceCheckOutMinutes: 15,
  considerLateAfterMinutes: 15,
  considerEarlyBeforeMinutes: 30,
  fullDayHours: 8,
  halfDayHours: 4,
  fullDayIfHoursAtLeast: 8,
  halfDayIfHoursLessThan: 4,
  halfDayMarkIfHoursBelow: 4,
  absentMarkIfHoursBelow: 2,
  autoAbsentMarkingEnabled: true,
  autoHalfDayMarkingEnabled: true,
  defaultMarkingForMissingPunch: "absent",
  missingPunchHandling: "auto_mark_default",
  punchMode: "double",
  checkInOutRule: "first_in_last_out",
  workHoursCalcMethod: "first_in_last_out",
  breakDeductionMethod: "fixed",
  breakFixedMinutes: 60,
  graceRulesEnabled: true,
  lateAllowedOccurrencesPerMonth: 3,
  earlyExitAllowedOccurrencesPerMonth: 3,
  latePenaltyAction: "none",
  earlyExitPenaltyAction: "none",
  autoLogoutEnabled: false,
  checkInMobile: true,
  checkInWeb: true,
};

export const ATTENDANCE_POLICY_UPDATED_EVENT = "hrms-attendance-policy-updated";

export function dispatchPolicyUpdated() {
  try {
    window.dispatchEvent(new CustomEvent(ATTENDANCE_POLICY_UPDATED_EVENT));
  } catch {
    /* ignore */
  }
}

export function isDemoAttendancePolicy(policy) {
  if (!policy) return false;
  return (
    policy.isDemo === true ||
    String(policy.id) === DEMO_POLICY_ID ||
    /demo auto attendance/i.test(String(policy.policyName || policy.name || ""))
  );
}

/**
 * No-op: kept so existing callers do not re-seed after delete.
 * @returns {{ installed: boolean, policyId: string, shiftId: string }}
 */
export function ensureDemoAttendancePolicy() {
  return { installed: false, policyId: DEMO_POLICY_ID, shiftId: DEMO_SHIFT_ID };
}

/**
 * Explicit QA install — only when a test/script asks for it.
 */
export function installDemoAttendancePolicy() {
  let installed = false;
  try {
    const shifts = loadAttendanceShifts();
    if (!shifts.some((s) => String(s.id) === DEMO_SHIFT_ID)) {
      localStorage.setItem(
        SHIFTS_STORAGE_KEY,
        JSON.stringify([{ ...DEMO_SHIFT }, ...shifts])
      );
      installed = true;
    }
    const policies = loadAttendancePolicies();
    if (!policies.some((p) => String(p.id) === DEMO_POLICY_ID)) {
      localStorage.setItem(
        POLICIES_STORAGE_KEY,
        JSON.stringify([{ ...DEMO_POLICY, updatedAt: new Date().toISOString() }, ...policies])
      );
      installed = true;
    }
  } catch {
    /* ignore */
  }
  if (installed) dispatchPolicyUpdated();
  return { installed, policyId: DEMO_POLICY_ID, shiftId: DEMO_SHIFT_ID };
}

export function getDemoPolicyTestGuide() {
  return {
    policyName: DEMO_POLICY.policyName,
    todayBehavior: "Punch to evaluate; past empty days → LOP (Auto path).",
    examplePresent: "Check in and out within shift → Present",
    exampleLop: "Past day with no punch → LOP",
  };
}
